<template>
  <div class="configs-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <div class="page-title">
          <h1>配置管理</h1>
          <p class="page-subtitle">管理系统配置参数，支持按类型分类管理</p>
        </div>
      </div>
      <div class="header-right">
        <el-button 
          type="info" 
          icon="el-icon-refresh" 
          size="medium"
          @click="refreshCache"
          :loading="refreshing"
          class="refresh-btn"
        >
          刷新缓存
        </el-button>
        <el-button 
          type="primary" 
          icon="el-icon-plus" 
          size="medium"
          @click="showAddDialog"
          class="add-btn"
        >
          添加配置
        </el-button>
      </div>
    </div>

    <!-- 配置类型导航 -->
    <div class="type-navigation">
      <div class="nav-container">
        <div 
          v-for="type in configTypes" 
          :key="type"
          :class="['nav-item', { active: activeType === type }]"
          @click="handleTypeChange({ name: type })"
        >
          <div class="nav-icon">
            <i :class="getTypeIcon(type)"></i>
          </div>
          <div class="nav-content">
            <span class="nav-label">{{ type }}</span>
            <span class="nav-count">{{ getTypeConfigCount(type) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 配置内容区域 -->
    <div class="content-area">
      <div class="content-header">
        <div class="content-title">
          <h2>{{ activeType }} 配置</h2>
          <span class="content-count">共 {{ getCurrentTypeConfigs().length }} 项配置</span>
        </div>
        <div class="content-search">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索配置键、值或描述..."
            prefix-icon="el-icon-search"
            class="search-input"
            clearable
            size="medium"
          />
        </div>
      </div>

      <div class="content-body" v-loading="loading">
        <div v-if="filteredConfigs.length === 0" class="empty-state">
          <div class="empty-illustration">
            <i class="el-icon-setting"></i>
          </div>
          <h3>暂无配置</h3>
          <p>当前类型下还没有配置项，点击上方按钮添加配置</p>
          <el-button type="primary" @click="showAddDialog" class="empty-action">
            立即添加
          </el-button>
        </div>
        
        <div v-else class="configs-grid">
          <div 
            v-for="config in filteredConfigs" 
            :key="config.id" 
            class="config-card"
          >
            <div class="card-header">
              <div class="config-info">
                <h3 class="config-key">{{ config.config_key }}</h3>
                <el-tag 
                  :type="getTypeTagType(config.type)" 
                  size="small"
                  class="type-tag"
                >
                  {{ config.type }}
                </el-tag>
              </div>
              <div class="card-actions">
                <el-tooltip content="编辑" placement="top">
                  <el-button 
                    size="small" 
                    icon="el-icon-edit" 
                    @click="editConfig(config)"
                    circle
                    class="action-btn edit-btn"
                  />
                </el-tooltip>
                <el-tooltip content="删除" placement="top">
                  <el-button 
                    size="small" 
                    type="danger" 
                    icon="el-icon-delete" 
                    @click="deleteConfig(config)"
                    circle
                    class="action-btn delete-btn"
                  />
                </el-tooltip>
              </div>
            </div>
            
            <div class="card-body">
              <div class="config-value">
                <span class="value-label">配置值：</span>
                <div class="value-content">{{ config.config_value }}</div>
              </div>
              
              <div v-if="config.description" class="config-description">
                <i class="el-icon-info"></i>
                <span>{{ config.description }}</span>
              </div>
            </div>
            
            <div class="card-footer">
              <div class="update-info">
                <i class="el-icon-time"></i>
                <span>更新于 {{ formatDate(config.updated_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑配置对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      :visible.sync="dialogVisible" 
      width="700px"
      :close-on-click-modal="false"
      class="config-dialog"
      :before-close="handleDialogClose"
    >
      <el-form 
        :model="configForm" 
        :rules="configRules" 
        ref="configForm" 
        label-width="120px"
        class="config-form"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="配置键" prop="config_key">
              <el-input 
                v-model="configForm.config_key" 
                placeholder="请输入配置键"
                maxlength="50"
                show-word-limit
                size="medium"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="配置类型" prop="type">
              <el-select 
                v-model="configForm.type" 
                placeholder="请选择配置类型"
                size="medium"
                style="width: 100%"
              >
                <el-option
                  v-for="type in configTypes"
                  :key="type"
                  :label="type"
                  :value="type"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item label="配置值" prop="config_value">
          <el-input 
            v-model="configForm.config_value" 
            type="textarea" 
            :rows="5" 
            placeholder="请输入配置值"
            maxlength="1000"
            show-word-limit
            size="medium"
          />
        </el-form-item>
        
        <el-form-item label="配置描述" prop="description">
          <el-input 
            v-model="configForm.description" 
            type="textarea"
            :rows="3"
            placeholder="请输入配置描述（可选）"
            maxlength="255"
            show-word-limit
            size="medium"
          />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" size="medium">取消</el-button>
        <el-button type="primary" @click="saveConfig" :loading="saving" size="medium">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getConfigs, getConfigTypes, createConfig, updateConfig, deleteConfig, refreshConfigCache } from '@/api/config'

export default {
  name: 'AdminConfigs',
  data() {
    return {
      loading: false,
      saving: false,
      refreshing: false,
      configs: [],
      configTypes: [],
      activeType: '',
      searchKeyword: '',
      dialogVisible: false,
      dialogTitle: '添加配置',
      isEdit: false,
      configForm: {
        config_key: '',
        config_value: '',
        description: '',
        type: ''
      },
      configRules: {
        config_key: [
          { required: true, message: '请输入配置键', trigger: 'blur' },
          { min: 1, max: 50, message: '配置键长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        config_value: [
          { required: true, message: '请输入配置值', trigger: 'blur' }
        ],
      }
    }
  },
  computed: {
    filteredConfigs() {
      const currentTypeConfigs = this.getCurrentTypeConfigs()
      if (!this.searchKeyword) {
        return currentTypeConfigs
      }
      return currentTypeConfigs.filter(config => 
        config.config_key.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        config.config_value.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        (config.description && config.description.toLowerCase().includes(this.searchKeyword.toLowerCase()))
      )
    }
  },
  mounted() {
    this.loadConfigs()
  },
  methods: {
    // 加载配置数据
    async loadConfigs() {
      this.loading = true
      try {
        const [configsResponse, typesResponse] = await Promise.all([
          getConfigs(),
          getConfigTypes()
        ])
        
        this.configs = configsResponse.data
        this.configTypes = typesResponse.data
        
        // 设置默认激活的类型
        if (this.configTypes.length > 0 && !this.activeType) {
          this.activeType = this.configTypes[0]
        }
      } catch (error) {
        this.$message.error('加载配置失败')
      } finally {
        this.loading = false
      }
    },

    // 获取当前类型的配置
    getCurrentTypeConfigs() {
      if (!this.activeType) return []
      return this.configs[this.activeType] || []
    },

    // 获取类型的配置数量
    getTypeConfigCount(type) {
      return this.configs[type] ? this.configs[type].length : 0
    },

    // 处理类型切换
    handleTypeChange(tab) {
      this.activeType = tab.name
      this.searchKeyword = ''
    },

    // 获取类型图标
    getTypeIcon(type) {
      const iconMap = {
        'system': 'el-icon-setting',
        'business': 'el-icon-s-shop',
        'security': 'el-icon-lock',
        'email': 'el-icon-message',
        'database': 'el-icon-coin',
        'api': 'el-icon-connection',
        'cache': 'el-icon-refresh',
        'log': 'el-icon-document',
        'other': 'el-icon-more'
      }
      return iconMap[type] || 'el-icon-setting'
    },

    // 获取类型标签颜色
    getTypeTagType(type) {
      const typeMap = {
        'system': 'primary',
        'business': 'success',
        'security': 'danger',
        'email': 'warning',
        'database': 'info',
        'api': '',
        'cache': 'success',
        'log': 'warning',
        'other': ''
      }
      return typeMap[type] || ''
    },

    // 显示添加对话框
    showAddDialog() {
      this.isEdit = false
      this.dialogTitle = '添加配置'
      this.configForm = {
        config_key: '',
        config_value: '',
        description: '',
        type: this.activeType || ''
      }
      this.dialogVisible = true
    },

    // 编辑配置
    editConfig(config) {
      this.isEdit = true
      this.dialogTitle = '编辑配置'
      this.configForm = {
        config_key: config.config_key,
        config_value: config.config_value,
        description: config.description,
        type: config.type
      }
      this.dialogVisible = true
    },

    // 删除配置
    async deleteConfig(config) {
      try {
        await this.$confirm('确定要删除这个配置吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await deleteConfig(config.id)
        this.$message.success('删除成功')
        this.loadConfigs()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    // 保存配置
    async saveConfig() {
      try {
        await this.$refs.configForm.validate()
        
        this.saving = true
        
        if (this.isEdit) {
          // 找到要编辑的配置ID
          const currentConfigs = this.getCurrentTypeConfigs()
          const configToEdit = currentConfigs.find(c => c.config_key === this.configForm.config_key)
          if (configToEdit) {
            await updateConfig(configToEdit.id, this.configForm)
            this.$message.success('更新成功')
          }
        } else {
          await createConfig(this.configForm)
          this.$message.success('添加成功')
        }
        
        this.dialogVisible = false
        this.loadConfigs()
      } catch (error) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // 处理对话框关闭
    handleDialogClose() {
      this.dialogVisible = false
      this.$refs.configForm.resetFields()
    },

    // 刷新Redis缓存
    async refreshCache() {
      try {
        this.refreshing = true
        await refreshConfigCache()
        this.$message.success('缓存刷新成功')
      } finally {
        this.refreshing = false
      }
    }
  }
}
</script>

<style scoped>
/* 页面整体布局 */
.configs-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 24px;
}

/* 页面头部 */
.page-header {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-left {
  flex: 1;
}

.page-title h1 {
  margin: 0 0 8px 0;
  color: #1a202c;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  margin: 0;
  color: #718096;
  font-size: 16px;
  font-weight: 400;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.6);
}

.refresh-btn {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(100, 116, 139, 0.4);
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(100, 116, 139, 0.6);
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
}

/* 类型导航 */
.type-navigation {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.nav-container {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  background: #f8fafc;
  min-width: 160px;
}

.nav-item:hover {
  background: #e2e8f0;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.nav-item.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-color: #667eea;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

.nav-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.nav-item.active .nav-icon {
  background: rgba(255, 255, 255, 0.3);
}

.nav-icon i {
  font-size: 20px;
}

.nav-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-label {
  font-weight: 600;
  font-size: 16px;
}

.nav-count {
  font-size: 14px;
  opacity: 0.8;
  font-weight: 400;
}

/* 内容区域 */
.content-area {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.content-header {
  padding: 32px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.content-title h2 {
  margin: 0 0 8px 0;
  color: #1a202c;
  font-size: 24px;
  font-weight: 600;
}

.content-count {
  color: #718096;
  font-size: 14px;
  font-weight: 400;
}

.content-search {
  display: flex;
  align-items: center;
}

.search-input {
  width: 320px;
}

.search-input .el-input__inner {
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
}

.search-input .el-input__inner:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* 内容主体 */
.content-body {
  padding: 32px;
  min-height: 400px;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #718096;
}

.empty-illustration {
  font-size: 80px;
  color: #cbd5e0;
  margin-bottom: 24px;
  opacity: 0.6;
}

.empty-state h3 {
  margin: 0 0 12px 0;
  color: #4a5568;
  font-size: 24px;
  font-weight: 600;
}

.empty-state p {
  margin: 0 0 24px 0;
  font-size: 16px;
  line-height: 1.6;
}

.empty-action {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

/* 配置网格 */
.configs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
}

/* 配置卡片 */
.config-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.config-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.config-card:hover {
  border-color: #667eea;
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15);
  transform: translateY(-4px);
}

.config-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.config-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.config-key {
  margin: 0;
  color: #1a202c;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-all;
}

.type-tag {
  align-self: flex-start;
  border-radius: 8px;
  font-weight: 500;
}

.card-actions {
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.config-card:hover .card-actions {
  opacity: 1;
}

.action-btn {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.edit-btn {
  background: #f0f9ff;
  border-color: #0ea5e9;
  color: #0ea5e9;
}

.edit-btn:hover {
  background: #0ea5e9;
  color: #fff;
  transform: scale(1.1);
}

.delete-btn:hover {
  transform: scale(1.1);
}

/* 卡片主体 */
.card-body {
  margin-bottom: 20px;
}

.config-value {
  margin-bottom: 16px;
}

.value-label {
  display: block;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.value-content {
  color: #2d3748;
  font-size: 15px;
  line-height: 1.6;
  word-break: break-all;
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  max-height: 120px;
  overflow-y: auto;
}

.config-description {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border-left: 4px solid #0ea5e9;
}

.config-description i {
  color: #0ea5e9;
  margin-top: 2px;
  flex-shrink: 0;
  font-size: 16px;
}

.config-description span {
  color: #0369a1;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
}

/* 卡片底部 */
.card-footer {
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.update-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 400;
}

.update-info i {
  font-size: 14px;
}

/* 对话框 */
.config-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.config-dialog .el-dialog__header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 24px 32px;
}

.config-dialog .el-dialog__title {
  color: #fff;
  font-weight: 600;
  font-size: 20px;
}

.config-dialog .el-dialog__body {
  padding: 32px;
  background: #f8fafc;
}

.config-form .el-form-item {
  margin-bottom: 24px;
}

.config-form .el-form-item__label {
  color: #4a5568;
  font-weight: 500;
}

.config-form .el-input__inner,
.config-form .el-textarea__inner {
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
}

.config-form .el-input__inner:focus,
.config-form .el-textarea__inner:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.dialog-footer {
  text-align: right;
  padding: 24px 32px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.dialog-footer .el-button {
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 500;
}

.dialog-footer .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

/* 响应式设计 */
@media (max-width: 1400px) {
  .configs-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}

@media (max-width: 1200px) {
  .configs-page {
    padding: 16px;
  }
  
  .configs-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
  }
  
  .nav-container {
    gap: 12px;
  }
  
  .nav-item {
    min-width: 140px;
    padding: 12px 16px;
  }
}

@media (max-width: 992px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 24px;
  }
  
  .header-right {
    width: 100%;
    justify-content: flex-end;
  }
  
  .nav-container {
    justify-content: center;
  }
  
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 24px;
  }
  
  .search-input {
    width: 100%;
  }
  
  .content-body {
    padding: 24px;
  }
  
  .configs-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .configs-page {
    padding: 12px;
  }
  
  .page-header {
    padding: 20px;
  }
  
  .page-title h1 {
    font-size: 24px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .type-navigation {
    padding: 16px;
  }
  
  .nav-container {
    gap: 8px;
  }
  
  .nav-item {
    min-width: 120px;
    padding: 10px 12px;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .nav-icon {
    width: 32px;
    height: 32px;
  }
  
  .nav-icon i {
    font-size: 16px;
  }
  
  .nav-label {
    font-size: 14px;
  }
  
  .nav-count {
    font-size: 12px;
  }
  
  .content-header {
    padding: 20px;
  }
  
  .content-title h2 {
    font-size: 20px;
  }
  
  .content-body {
    padding: 20px;
  }
  
  .config-card {
    padding: 20px;
  }
  
  .config-key {
    font-size: 16px;
  }
  
  .card-actions {
    opacity: 1;
  }
  
  .config-dialog .el-dialog__body {
    padding: 20px;
  }
  
  .dialog-footer {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .configs-page {
    padding: 8px;
  }
  
  .page-header {
    padding: 16px;
  }
  
  .page-title h1 {
    font-size: 20px;
  }
  
  .type-navigation {
    padding: 12px;
  }
  
  .nav-item {
    min-width: 100px;
    padding: 8px 10px;
  }
  
  .nav-icon {
    width: 28px;
    height: 28px;
  }
  
  .nav-icon i {
    font-size: 14px;
  }
  
  .nav-label {
    font-size: 12px;
  }
  
  .nav-count {
    font-size: 10px;
  }
  
  .content-header {
    padding: 16px;
  }
  
  .content-body {
    padding: 16px;
  }
  
  .config-card {
    padding: 16px;
  }
  
  .config-dialog .el-dialog__body {
    padding: 16px;
  }
  
  .dialog-footer {
    padding: 16px;
  }
}
</style>